'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import LayoutStyle from './style';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CollapseContainer, CollapseItem, Container, Text, useResizeObserver, useTranslation, useUi } from '@weareconceptstudio/core';
import CustomLink from '@/components/CustomLink';
import { usePathname } from 'next/navigation';
import TitleAnim from '@/animation';
import gsap from 'gsap/all';

const Layout = ({ children, leftBarData = [] }) => {
	const { winWidth } = useUi();
	const pathname = usePathname();
	const { selectedLang } = useTranslation();

	const titleRef = useRef(leftBarData.map(() => React.createRef()));
	const arrowRef = useRef(leftBarData.map(() => React.createRef()));

	const collapseItemRef = useRef(leftBarData?.map((section) => section.items?.map(() => React.createRef())));

	const leftRef = useRef();
	const rightRef = useRef();
	const triggerRef = useRef();
	const leftRefInnerRef = useRef();

	const [dimensions, setDimensions] = useState({
		headerHeight: 0,
	});
	const { headerHeight } = dimensions;
	const resizes = useResizeObserver(typeof window !== 'undefined' ? document.querySelector('.right-inner-block') : null);

	const cleanedPathname = useMemo(() => {
		let cleaned = pathname;
		if (selectedLang) {
			// Remove language prefix from the beginning of the path
			cleaned = pathname.replace(new RegExp(`^/${selectedLang}`), '');
		}
		return cleaned;
	}, [pathname, selectedLang]);

	const activeSlug = useMemo(() => {
		const parts = cleanedPathname.split('/').filter(Boolean);
		return parts[parts.length - 1] || '';
	}, [cleanedPathname]);

	//! Calculate element sizes
	useEffect(() => {
		if (typeof window === 'undefined') return;

		const getElementSize = (selector, prop = 'clientHeight') => document.querySelector(selector)?.[prop] ?? 0;

		setDimensions({
			headerHeight: getElementSize('header'),
		});
	}, [winWidth]);

	//! Pin share
	useEffect(() => {
		if (winWidth >= 1024) {
			if (leftRef.current && rightRef.current) {
				const endTrigger = document.querySelector('.right-inner-block');
				const endMarker = leftRef.current.offsetHeight;
				const headerPadding = headerHeight;

				leftRef.current.style.cssText = `padding-top: ${headerPadding}px; margin-top: -${headerPadding}px;`;

				if (endTrigger) {
					triggerRef.current = ScrollTrigger.create({
						start: `top top`,
						end: `bottom top+=${endMarker + headerPadding + 'px'}`,
						trigger: leftRef.current,
						// markers: true,
						pin: true,
						pinSpacing: false,
						endTrigger: endTrigger,
					});
				}
			}
		}

		return () => {
			if (triggerRef.current) {
				triggerRef.current.kill();
			}
		};
	}, [winWidth, resizes]);

	useEffect(() => {
		if (typeof window === 'undefined' && !leftRef.current) return;
		const titleElements = leftRef.current.querySelectorAll('.opacity-zero');
		titleElements.forEach((element) => {
			element.classList.remove('opacity-zero');
		});
	}, [winWidth]);

	useEffect(() => {
		if (leftRefInnerRef.current) {
			gsap.fromTo(leftRefInnerRef.current, { opacity: 0 }, { opacity: 1, duration: 1, delay: 0.1, ease: 'power2.out' });
		}
	}, []);

	return (
		<LayoutStyle className='layout-container'>
			{titleRef.current.map((ref, index) => {
				return (
					<TitleAnim
						resize={false}
						key={`${index}`}
						animType='subtitle'
						animRef={ref}
						finishPercent={0.4}
						configAnim={{ delay: index * 0.1 }}
						onFinish={() => {
							arrowRef.current[index]?.current?.classList?.add('active');
						}}
						onBack={() => {
							arrowRef.current[index]?.current?.classList?.remove('active');
						}}
					/>
				);
			})}

			{collapseItemRef.current.map((sectionRefs, sectionIndex) => {
				return sectionRefs?.map((ref, childIndex) => {
					return (
						<TitleAnim
							resize={false}
							key={`${sectionIndex}-${childIndex}`}
							finishPercent={0.6}
							toVars={{ delay: sectionIndex * 0.15 }}
							animType='subtitle'
							triggerRef={titleRef.current[sectionIndex]}
							animRef={ref}
						/>
					);
				});
			})}

			<div className='layout-inner-container'>
				<Container
					row
					isSection>
					<div className='left-block col-4 col-dx-12'>
						<div
							ref={leftRefInnerRef}
							className='left-inner-block opacity-zero'>
							<div
								key={`left-bar`}
								ref={leftRef}>
								<div className='content'>
									<div className='first-block'>
										{leftBarData.map(({ items, title, slug }, index) => {
											if (items && items.length === 0) {
												return null;
											}

											const isActiveGroup = items?.some((item) => item.slug === activeSlug);

											return items?.length > 0 ? (
												<CollapseContainer key={title + slug}>
													<div className='collapse-item-wrap'>
														<CollapseItem
															status={isActiveGroup ? 'open' : 'close'}
															title={
																<div className={`wrapper-title-button ${isActiveGroup ? 'active' : ''}`}>
																	<Text
																		ref={titleRef.current[index]}
																		className={`h7 montserrat-400 black-800 opacity-animation text-titles`}>
																		{title}
																	</Text>

																	<svg
																		xmlns='http://www.w3.org/2000/svg'
																		width='11'
																		height='7'
																		viewBox='0 0 11 7'
																		fill='none'
																		ref={arrowRef.current[index]}
																		className='opacity-zero opacity-animation'>
																		<path
																			d='M10.293 1.88609L5.29297 6.5L0.292969 1.88609L1.79504 0.5L5.29297 3.72782L8.79089 0.5L10.293 1.88609Z'
																			fill='var(--black900)'
																		/>
																	</svg>
																</div>
															}
															description={
																<div className='collapse-item-description'>
																	{items?.map(({ title, slug }, idx) => (
																		<CustomLink
																			key={idx}
																			url={`/business-environment/incentives/${slug}`}
																			className={`collapse-item-link ${activeSlug === slug ? 'active' : ''}`}>
																			<Text
																				ref={collapseItemRef.current[index][idx]}
																				className='p p2 montserrat-400 black-900 opacity-animation'>
																				{title}
																			</Text>
																		</CustomLink>
																	))}
																</div>
															}
														/>
													</div>
												</CollapseContainer>
											) : (
												<CustomLink
													key={slug + title}
													url={`/business-environment/incentives/${slug}`}
													className={`bar-item ${cleanedPathname.includes(slug) ? 'active' : ''}`}>
													<Text
														ref={titleRef.current[index]}
														className='h7 montserrat-400 black-800 opacity-animation'>
														{title}
													</Text>
												</CustomLink>
											);
										})}
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className='right-block col-8 col-dx-12'>
						<div
							ref={rightRef}
							className='right-inner-block'>
							{children}
						</div>
					</div>
				</Container>
			</div>
		</LayoutStyle>
	);
};

export default Layout;
